const CHOICE_INPUT_TYPES = ["dropdown", "radio", "checkbox"];

export const isChoiceInputType = (type) => CHOICE_INPUT_TYPES.includes(type);

export const getInputCondition = (input) => {
  if (!input?.condition || !Array.isArray(input.condition)) return null;
  return input.condition[0] || null;
};

export const buildInputConditionPayload = ({
  enabled,
  sourceIndex,
  response,
}) => {
  if (!enabled) return null;

  if (sourceIndex === undefined || sourceIndex === null || sourceIndex < 0) {
    return null;
  }

  if (response === undefined || response === null || response === "") {
    return null;
  }

  return [
    {
      trigger: true,
      input_index: Number(sourceIndex),
      response,
    },
  ];
};

export const normalizeResponseForCondition = (value) => {
  if (value === undefined || value === null || value === "") {
    return value;
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "object") {
    if (Array.isArray(value.selections)) {
      return value.selections;
    }

    if (value.selection !== undefined && value.selection !== null) {
      return value.selection;
    }
  }

  return value;
};

export const responseValueMatchesCondition = (value, expected) => {
  if (expected === undefined || expected === null || expected === "") {
    return false;
  }

  const normalized = normalizeResponseForCondition(value);

  if (Array.isArray(normalized)) {
    return normalized.map(String).includes(String(expected));
  }

  if (normalized === undefined || normalized === null || normalized === "") {
    return false;
  }

  return String(normalized) === String(expected);
};

/** Flatten Form responses whether stored as array or object keyed by index. */
export const getResponseValuesList = (responseValues = []) => {
  if (Array.isArray(responseValues)) {
    return responseValues;
  }

  if (!responseValues || typeof responseValues !== "object") {
    return [];
  }

  return Object.keys(responseValues)
    .filter((key) => /^\d+$/.test(String(key)))
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => responseValues[key]);
};

export const anyResponseMatchesCondition = (responseValues, expected) =>
  getResponseValuesList(responseValues).some((value) =>
    responseValueMatchesCondition(value, expected),
  );

export const isInputConditionMet = (input, responseValues = []) => {
  const condition = getInputCondition(input);

  if (!condition?.trigger) return true;

  const sourceIndex = Number(condition.input_index);

  if (!Number.isInteger(sourceIndex) || sourceIndex < 0) return true;

  const sourceValue = Array.isArray(responseValues)
    ? responseValues[sourceIndex]
    : (responseValues?.[sourceIndex] ?? responseValues?.[String(sourceIndex)]);

  return responseValueMatchesCondition(sourceValue, condition.response);
};

export const getVisibleInputEntries = (inputs = [], responseValues = []) =>
  (inputs || [])
    .map((input, index) => ({ input, index }))
    .filter(({ input }) => isInputConditionMet(input, responseValues));

export const normalizeConditionId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "object") {
    return (
      value.$oid ||
      value.oid ||
      (value._id != null ? normalizeConditionId(value._id) : null) ||
      (value.id != null ? normalizeConditionId(value.id) : null)
    );
  }
  return null;
};

/**
 * Find the next question targeted by a question-level conditional trigger
 * based on answers on the current question.
 */
export const findConditionalNextQuestion = (
  questions = [],
  currentQuestion,
  responseValues = [],
) => {
  const currentId = normalizeConditionId(currentQuestion?._id);

  if (!currentId) return null;

  return (
    (questions || []).find((question) => {
      const condition = question?.condition?.[0];

      if (!condition?.trigger) return false;
      if (normalizeConditionId(condition.question) !== currentId) return false;

      return anyResponseMatchesCondition(responseValues, condition.response);
    }) || null
  );
};

export const isCurrentQuestionConditionSource = (
  questions = [],
  currentQuestion,
) => {
  const currentId = normalizeConditionId(currentQuestion?._id);

  if (!currentId) return false;

  return (questions || []).some((question) =>
    (question?.condition || []).some(
      (condition) => normalizeConditionId(condition?.question) === currentId,
    ),
  );
};
